/**
 * GiftPage — the Member Plus gift (mct_28580188-92f, mct_2e165cd5-0ec).
 *
 *   /gift/:campaign   claim: first name, last name, email → "check your inbox"
 *   /gift/confirm?t=  the emailed link: one button starts the two months
 *
 * Michael, 3 Nov 2026: two months of Member Plus, "not have them have to do
 * anything major. like put in their credit card or go through Spiffy". Anyone
 * can type anyone's address, so the claim only emails a confirm link; the
 * gift starts when that person presses the button here (a POST, so a mail
 * scanner opening the link starts nothing). The claim always answers "check
 * your inbox", so this page never says who is already a member.
 *
 * Every heading and paragraph comes from the API, which reads Command
 * Centre's member_journey blocks. The only Engine words are the fallbacks for
 * a closed, expired or not-ready link, and the form labels.
 * SOP: seeds/sops/sop-ee-member-journey.md, "The gift".
 */

import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import TakerShell from './components/TakerShell';

const API_BASE = (import.meta as unknown as { env: Record<string, string> }).env?.VITE_API_BASE_URL ?? '';
const ACCENT = '#F05A28';
const MUTED = '#6b7280';

interface PageCopy { title: string; body: string }
interface Giver { name: string; title: string | null; photo_url: string | null; team_page_url: string | null; linkedin_url: string | null }
interface Value { monthly: number; months: number; total: number; currency: string; includes: string[] }

const NAVY = '#0A0A2E';
const money = (n: number, cur: string) => `${cur === 'USD' ? 'US$' : `${cur} `}${Number.isInteger(n) ? n : n.toFixed(2)}`;

/* The claim page's frame: the story on the left, the form on the right,
   stacked on a phone. The giver, the value and what is included are facts
   read from Command Centre (team profile and pricing); the heading and
   paragraphs are CC's gift_claim_page block. */
const css = `
.gift-wrap{max-width:1040px;margin:0 auto;padding:40px 16px 72px;display:grid;grid-template-columns:minmax(0,1.25fr) minmax(0,1fr);gap:36px;align-items:start}
.gift-card{background:#fff;border-radius:18px;box-shadow:0 10px 30px rgba(10,10,46,.08);padding:28px}
.gift-form{position:sticky;top:24px}
.gift-giver{display:flex;gap:16px;align-items:center;margin-bottom:22px}
.gift-giver img{width:84px;height:84px;border-radius:50%;object-fit:cover;border:3px solid #fff;box-shadow:0 0 0 3px ${ACCENT}}
.gift-kicker{font-size:12px;letter-spacing:.12em;text-transform:uppercase;color:${ACCENT};font-weight:700}
.gift-name{font-family:'Fraunces',Georgia,serif;font-size:24px;font-weight:700;line-height:1.15;margin:2px 0}
.gift-links{display:flex;gap:14px;flex-wrap:wrap;font-size:14px;margin-top:4px}
.gift-links a{color:${NAVY};font-weight:600;text-decoration:none;border-bottom:2px solid ${ACCENT}33}
.gift-h1{font-family:'Fraunces',Georgia,serif;font-size:38px;line-height:1.1;margin:0 0 16px;color:${NAVY};text-wrap:balance}
.gift-facts{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:10px;margin:22px 0}
.gift-fact{background:#FFF4EE;border-radius:12px;padding:12px 14px}
.gift-fact b{display:block;font-size:20px;color:${NAVY}}
.gift-fact span{font-size:13px;color:#475569}
.gift-incl{list-style:none;padding:0;margin:12px 0 0;display:grid;gap:8px}
.gift-incl li{display:flex;gap:10px;align-items:flex-start;line-height:1.45;color:#334155}
.gift-incl li:before{content:'';flex:0 0 18px;height:18px;margin-top:2px;border-radius:50%;background:${ACCENT} url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'%3E%3Cpath d='M5 10.5l3 3 7-7' fill='none' stroke='white' stroke-width='2.4' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E") center/14px no-repeat}
.gift-more{display:inline-block;margin-top:16px;color:${ACCENT};font-weight:600}
@media (max-width:820px){.gift-wrap{grid-template-columns:1fr;padding-top:24px}.gift-form{position:static}.gift-h1{font-size:30px}.gift-facts{grid-template-columns:1fr}}
`;

const label: React.CSSProperties = { display: 'block', fontWeight: 600, margin: '18px 0 6px' };
const input: React.CSSProperties = { width: '100%', padding: '10px 12px', fontSize: 16, borderRadius: 8, border: '1px solid #CBD5E1', background: '#fff', boxSizing: 'border-box' };
const button: React.CSSProperties = {
  marginTop: 24, padding: '12px 22px', borderRadius: 8, border: 'none', background: ACCENT, color: '#fff', fontSize: 16, fontWeight: 600, cursor: 'pointer',
};

/** CC's body: paragraphs, **bold** and [links](https://…) only. */
function Body({ text }: { text: string }) {
  return (
    <>
      {text.split(/\n{2,}/).map((para, i) => (
        <p key={i} style={{ lineHeight: 1.6, color: '#334155', margin: '0 0 12px' }}>
          {para.split(/(\*\*[^*]+\*\*|\[[^\]]+\]\(https:\/\/[^)\s]+\))/g).map((bit, j) => {
            const b = bit.match(/^\*\*([^*]+)\*\*$/);
            if (b) return <strong key={j}>{b[1]}</strong>;
            const a = bit.match(/^\[([^\]]+)\]\((https:\/\/[^)\s]+)\)$/);
            if (a) return <a key={j} href={a[2]} style={{ color: ACCENT }}>{a[1]}</a>;
            return <span key={j}>{bit}</span>;
          })}
        </p>
      ))}
    </>
  );
}

function Message({ title, text }: { title: string; text: string }) {
  return (
    <>
      <h1 style={{ fontSize: 28, margin: '0 0 12px' }}>{title}</h1>
      <p style={{ lineHeight: 1.6 }}>{text}</p>
    </>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <TakerShell>
      <main style={{ maxWidth: 560, margin: '0 auto', padding: '48px 16px 72px' }} data-testid="gift-page">{children}</main>
    </TakerShell>
  );
}

export function GiftClaimPage() {
  const { campaign = '' } = useParams();
  const [phase, setPhase] = useState<'loading' | 'open' | 'closed' | 'not_ready' | 'sent' | 'error'>('loading');
  const [copy, setCopy] = useState<PageCopy | null>(null);
  const [giver, setGiver] = useState<Giver | null>(null);
  const [value, setValue] = useState<Value | null>(null);
  const [plusUrl, setPlusUrl] = useState<string | null>(null);
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    fetch(`${API_BASE}/api/public/member-gift?campaign=${encodeURIComponent(campaign)}`)
      .then(async (r) => {
        if (!live) return;
        const d = (await r.json().catch(() => ({}))) as { state?: string; page?: PageCopy; giver?: Giver | null; value?: Value | null; plus_url?: string };
        if (d.state === 'open' && d.page) {
          setCopy(d.page); setGiver(d.giver ?? null); setValue(d.value ?? null); setPlusUrl(d.plus_url ?? null); setPhase('open');
        }
        else setPhase(d.state === 'not_ready' ? 'not_ready' : 'closed');
      })
      .catch(() => live && setPhase('error'));
    return () => { live = false; };
  }, [campaign]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      const r = await fetch(`${API_BASE}/api/public/member-gift/claim`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...form, campaign }),
      });
      const d = (await r.json().catch(() => ({}))) as { state?: string; error?: string };
      if (!r.ok) { setError(d.error ?? 'That did not go through. Please try again.'); return; }
      setPhase(d.state === 'closed' ? 'closed' : 'sent');
    } catch {
      setError('That did not go through. Please try again.');
    } finally { setBusy(false); }
  };

  if (phase === 'open' && copy) {
    return (
      <TakerShell>
        <style>{css}</style>
        <div className="gift-wrap" data-testid="gift-page">
          <section>
            {giver && (
              <div className="gift-giver" data-testid="gift-giver">
                {giver.photo_url && <img src={giver.photo_url} alt={giver.name} />}
                <div>
                  <div className="gift-kicker">A gift from</div>
                  <div className="gift-name">{giver.name}</div>
                  {giver.title && <div style={{ color: MUTED, fontSize: 14 }}>{giver.title}, The Successful Bookkeeper</div>}
                  <div className="gift-links">
                    {giver.linkedin_url && <a href={giver.linkedin_url} target="_blank" rel="noreferrer">LinkedIn</a>}
                    {giver.team_page_url && <a href={giver.team_page_url} target="_blank" rel="noreferrer">About {giver.name.split(' ')[0]}</a>}
                  </div>
                </div>
              </div>
            )}
            <h1 className="gift-h1">{copy.title}</h1>
            <Body text={copy.body} />
            {value && (
              <div className="gift-facts" data-testid="gift-facts">
                <div className="gift-fact"><b>{money(value.total, value.currency)}</b><span>{value.months} months of Member Plus, yours free</span></div>
                <div className="gift-fact"><b>No card</b><span>No payment details, no checkout</span></div>
                <div className="gift-fact"><b>Nothing to pay</b><span>No catch and nothing expected afterwards</span></div>
              </div>
            )}
            {value && value.includes.length > 0 && (
              <div className="gift-card" style={{ padding: 24 }}>
                <div className="gift-kicker">What your two months include</div>
                <ul className="gift-incl">{value.includes.map((c) => <li key={c}>{c}</li>)}</ul>
                {plusUrl && <a className="gift-more" href={plusUrl} target="_blank" rel="noreferrer">See everything in Member Plus</a>}
              </div>
            )}
          </section>
          <aside className="gift-card gift-form">
            <form onSubmit={submit} data-testid="gift-claim-form">
              <div className="gift-kicker">Claim your gift</div>
              <p style={{ margin: '6px 0 0', color: '#334155', lineHeight: 1.5 }}>Tell us where to send it. We email you one link, and your two months start when you press it.</p>
              <label style={label} htmlFor="gift-first">First name</label>
              <input id="gift-first" style={input} required maxLength={80} autoComplete="given-name" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
              <label style={label} htmlFor="gift-last">Last name</label>
              <input id="gift-last" style={input} maxLength={80} autoComplete="family-name" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
              <label style={label} htmlFor="gift-email">Email</label>
              <input id="gift-email" style={input} type="email" required maxLength={200} autoComplete="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              {error && <p role="alert" style={{ color: '#b91c1c', marginTop: 12 }}>{error}</p>}
              <button type="submit" style={{ ...button, width: '100%' }} disabled={busy}>{busy ? 'Sending…' : 'Send me the link'}</button>
              <p style={{ fontSize: 13, color: MUTED, marginTop: 12, lineHeight: 1.5 }}>No credit card. Nothing to pay, now or later.</p>
            </form>
          </aside>
        </div>
      </TakerShell>
    );
  }

  return (
    <Shell>
      {phase === 'loading' && <p style={{ color: MUTED }}>Loading…</p>}
      {phase === 'error' && <Message title="Your Member Plus gift" text="Something went wrong loading this page. Please try again in a moment." />}
      {phase === 'closed' && <Message title="Your Member Plus gift" text="This gift is not open right now. If you were sent this link, reply to the message it came in and we will sort it out." />}
      {phase === 'not_ready' && <Message title="Your Member Plus gift" text="This page is not quite ready. Please try again a little later." />}
      {phase === 'sent' && <Message title="Check your inbox" text={`We have emailed ${form.email} a link to start your two months. It is good for seven days.`} />}
    </Shell>
  );
}

type ConfirmState = 'loading' | 'ready' | 'done' | 'already_member' | 'expired' | 'invalid' | 'closed' | 'full' | 'not_ready' | 'error';

export function GiftConfirmPage() {
  const [params, setParams] = useSearchParams();
  // Read the token once, then take it off the address bar and send no referrer.
  const [token] = useState(() => params.get('t') ?? '');
  useEffect(() => {
    if (params.has('t')) setParams({}, { replace: true });
    const meta = document.createElement('meta');
    meta.name = 'referrer'; meta.content = 'no-referrer';
    document.head.appendChild(meta);
    return () => { meta.remove(); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [state, setState] = useState<ConfirmState>('loading');
  const [copy, setCopy] = useState<PageCopy | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const apply = (d: { state?: string; page?: PageCopy | null }) => {
    setCopy(d.page ?? null);
    setState((d.state as ConfirmState) ?? 'error');
  };

  useEffect(() => {
    let live = true;
    fetch(`${API_BASE}/api/public/member-gift/confirm?t=${encodeURIComponent(token)}`)
      .then(async (r) => { if (live) apply(await r.json().catch(() => ({ state: 'error' }))); })
      .catch(() => live && setState('error'));
    return () => { live = false; };
  }, [token]);

  const start = async () => {
    setBusy(true); setError(null);
    try {
      const r = await fetch(`${API_BASE}/api/public/member-gift/confirm`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ t: token }),
      });
      const d = (await r.json().catch(() => ({}))) as { state?: string; page?: PageCopy | null; error?: string };
      if (!r.ok) { setError(d.error ?? 'That did not go through. Please try again.'); return; }
      apply(d);
    } catch {
      setError('That did not go through. Please try again.');
    } finally { setBusy(false); }
  };

  return (
    <Shell>
      {state === 'loading' && <p style={{ color: MUTED }}>Loading…</p>}
      {(state === 'ready' || state === 'done' || state === 'already_member') && copy && (
        <>
          <h1 style={{ fontSize: 28, margin: '0 0 12px' }}>{copy.title}</h1>
          <Body text={copy.body} />
          {state === 'ready' && (
            <>
              {error && <p role="alert" style={{ color: '#b91c1c' }}>{error}</p>}
              <button type="button" style={button} disabled={busy} onClick={start} data-testid="gift-start">{busy ? 'Starting…' : 'Start my two months'}</button>
            </>
          )}
        </>
      )}
      {(state === 'ready' || state === 'done' || state === 'already_member') && !copy && <Message title="Your Member Plus gift" text="This page is not quite ready. Please try your link again a little later." />}
      {state === 'expired' && <Message title="This link has expired" text="Gift links last seven days. Go back to the page you started from and ask for a fresh one." />}
      {state === 'invalid' && <Message title="We could not find this gift" text="This link is not one we recognise. Please use the link from your most recent email." />}
      {state === 'closed' && <Message title="Your Member Plus gift" text="This gift is not open right now. Reply to the message you were sent and we will sort it out." />}
      {state === 'full' && <Message title="All the gifts have been claimed" text="We are sorry: every one of these gifts has been taken. Reply to the message you were sent and we will see what we can do." />}
      {(state === 'not_ready' || state === 'error') && <Message title="Your Member Plus gift" text="Something went wrong just now. Please try your link again in a moment." />}
    </Shell>
  );
}
